import { createAction } from 'redux-actions';
import moment from 'moment';
import { replace } from 'react-router-redux';

import { openSuccessAlert, openFailedAlert, closeAlert } from 'actions/alert';
import { wrapFetchAuth, wrapFetchFormData, SELF_HOST_ENDPOINT } from 'actions/endpoint';

import {
	keyTransform,
	serverMapEmpty,
	mapGenderLabel,
	mapUserLevel,
	normalizeArray,
	covertSocialType,
	mapSocialType,
	toCamelCase,
} from 'util/helper';

import { examUserData, examUserCertificateData } from 'util/exam';
import { mapCountryCode } from 'util/country/util';
import AllCountries from 'util/country';
import { t, languageMapRoute } from 'util/i18n';
import { sessionStorage } from 'util/storage';

export const showAlert = createAction('SHOW_ALERT', (message, success) => ({
	show: true,
	message,
	success,
}));

export const getUser = createAction('FETCH_USER', () => async dispatch => {
	const { status, message, data } = await wrapFetchAuth('user', {
		method: 'GET',
	});

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	const transData = keyTransform(data);

	if (mapUserLevel(transData.levelId) === 'REGISTERED') {
		dispatch(openSuccessAlert('settings', t('settings:welcomeText')));
	}

	if (mapUserLevel(transData.levelId) === 'DENIED') {
		dispatch(openFailedAlert('settings', t('settings:deniedText')));
	}

	const favoriteData = {};
	const songData = {};

	const songList = transData.songs.map(item => {
		favoriteData[item.spotify_id] = item.id;
		songData[item.spotify_id] = {
			name: item.name,
			album: {
				name: item.album_name,
				images: [{ id: 0, url: item.album_image }],
			},
			artists: item.artist_name.split(', ').map(name => ({ name })),
		};

		return item.spotify_id;
	});

	const socialData = {};
	const socialList = transData.socialMedia.map(item => {
		socialData[item.id] = { ...item, type: mapSocialType(item.type) };
		return item.id;
	});

	const certificate = {
		type: [],
		value: transData.certificateNumber === null ? '' : transData.certificateNumber,
	};

	if (transData.certificateType !== null) {
		certificate.type.push({
			value: transData.certificateType,
			label: t(`certificateType:${transData.certificateType}`),
		});
	}

	return {
		name: { value: serverMapEmpty(transData.name) },
		gender: { value: { value: transData.gender, label: mapGenderLabel(transData.gender) } },
		jobTypes: { value: transData.jobTypes.map(item => ({ value: item.id, label: item.name })) },
		birth: {
			value:
				serverMapEmpty(transData.birth) !== ''
					? moment(serverMapEmpty(transData.birth), 'YYYY/MM/DD').format('X')
					: '',
		},
		country: {
			value:
				transData.country === null
					? []
					: [{ value: transData.country, label: mapCountryCode(transData.country) }],
		},
		certificate,
		professions: {
			value: transData.professions.map(item => ({ value: item.id, label: item.name })),
		},
		phone: {
			value: serverMapEmpty(transData.phone),
			country: AllCountries.getCountryByCode(transData.phoneCountry),
		},
		expect: { value: serverMapEmpty(transData.expect) },
		open: { value: transData.open === 1 },
		nickname: { value: serverMapEmpty(transData.nickname) },
		company: { value: serverMapEmpty(transData.company) },
		career: {
			value: transData.careers.map(item => ({ value: item.id, label: item.name })),
		},
		intro: { value: serverMapEmpty(transData.intro) },
		interests: { value: transData.interests.map(item => ({ value: item.id, label: item.name })) },
		songs: { value: songList, favoriteData },
		socialMedia: { value: socialList, data: socialData, latestIndex: -1 },
		image: { value: serverMapEmpty(transData.image), url: serverMapEmpty(transData.imageUrl) },
		data: transData,

		songData,

		social: { list: socialList, data: socialData, latestIndex: -1 },
	};
});

const generateUserData = (lan, userData, songData) => ({
	name: userData.name.value,
	gender: userData.gender.value.value,
	job_types: userData.jobTypes.value.map(item => ({ id: item.value })),
	birth: userData.birth.value !== '' ? moment(userData.birth.value, 'X').format('YYYY/MM/DD') : '',
	country: userData.country.value.length > 0 ? userData.country.value[0].value : '',
	certificate_type: userData.certificate.type.length > 0 ? userData.certificate.type[0].value : '',
	certificate_number: userData.certificate.value,
	professions: userData.professions.value.map(item => ({ id: item.value })),
	phone: userData.phone.value,
	phone_country: userData.phone.country.iso2,
	expect: userData.expect.value,
	open: userData.open.value ? 1 : 0,
	nickname: userData.nickname.value,
	company: userData.company.value,
	careers: userData.career.value.map(item => ({ id: item.value })),
	intro: userData.intro.value,
	interests: userData.interests.value.map(item => ({ id: item.value })),
	songs: userData.songs.value.map(id => ({
		id: Object.keys(userData.songs.favoriteData).includes(id)
			? userData.songs.favoriteData[id]
			: null,
		spotify_id: id,
		name: songData[id].name,
		album_name: songData[id].album.name,
		album_image: songData[id].album.images.length > 0 ? songData[id].album.images[0].url : '',
		artist_name: songData[id].artists.map(artist => artist.name).join(', '),
	})),
	social_media: userData.socialMedia.value.map(id => {
		if (id < 0) {
			return {
				id: null,
				url: userData.socialMedia.data[id].url,
				type: covertSocialType(userData.socialMedia.data[id].type),
			};
		}

		return {
			...userData.socialMedia.data[id],
			type: covertSocialType(userData.socialMedia.data[id].type),
		};
	}),

	image: userData.image.value,
});

const validateUserData = createAction('VALIDATE_USER_DATA', (key, valid) => ({ key, valid }));

const clearValidateUserData = createAction('CLEAR_VALIDATE_USER_DATA');

export const applyUserDataProcess = createAction(
	'APPLY_USER_DATA_PROCESS',
	() => async (dispatch, getState) => {
		const {
			i18n: { lan },
			user: { form, data: { levelId }, userIgData },
			song: { data: songData },
		} = getState();

		const userData = generateUserData(lan, form, songData);

		const userCheckData = examUserData(userData, userIgData, form.phone);

		dispatch(clearValidateUserData());
		dispatch(closeAlert('settings'));

		if (!userCheckData.value) {
			dispatch(openFailedAlert('settings', t('settings:notValidText')));
			userCheckData.notValid.forEach(key => dispatch(validateUserData(toCamelCase(key), false)));

			window.scrollTo(0, 0);
			document.activeElement.blur();
			return;
		}

		const userCheckCertificateData = examUserCertificateData(userData);

		if (!userCheckCertificateData.value) {
			dispatch(openFailedAlert('settings', t('settings:notValidCertificate')));
			dispatch(validateUserData('certificate', false));

			window.scrollTo(0, 0);
			document.activeElement.blur();
			return;
		}

		let result = {};

		if (['REGISTERED', 'DENIED'].includes(mapUserLevel(levelId))) {
			result = await wrapFetchAuth('user/apply', {
				method: 'POST',
				body: JSON.stringify(userData),
			});
		} else {
			result = await wrapFetchAuth('user/update', {
				method: 'POST',
				body: JSON.stringify(userData),
			});
		}

		if (result.status !== 200) {
			dispatch(openFailedAlert('settings', result.message));
			window.scrollTo(0, 0);
			document.activeElement.blur();
			return;
		}

		sessionStorage.removeItem('userForm');

		dispatch(getUser());
		window.scrollTo(0, 0);
		document.activeElement.blur();
	},
);

export const getUserJobTypeOptionData = createAction(
	'FETCH_USER_JOB_TYPE_OPTION_DATA',
	async () => {
		const { status, message, data } = await wrapFetchAuth('user/getJobType', {
			method: 'GET',
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return {
			jobTypes: normalizeArray(data),
		};
	},
);

export const getUserInterestOptionData = createAction(
	'FETCH_USER_INTEREST_OPTION_DATA',
	async (name = '') => {
		const { status, message, data } = await wrapFetchAuth(
			'user/getInterest',
			{
				method: 'GET',
			},
			name !== '' ? { name } : {},
		);

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return {
			interests: normalizeArray(data),
		};
	},
);

export const updateUserInterestOptionData = createAction(
	'UPDATE_USER_INTEREST_OPTION_DATA',
	(name = '') => async (dispatch, getState) => {
		const { user: { form: { interests } } } = getState();

		const existValue = interests.value.filter(
			item => item.label.toLowerCase() === name.toLowerCase(),
		);

		if (existValue.length > 0) {
			return { value: interests.value };
		}

		const existOption = interests.list
			.filter(id => interests.data[id].name.toLowerCase() === name.toLowerCase())
			.map(id => ({ value: id, label: interests.data[id].name }));

		if (existOption.length > 0) {
			dispatch(getUserInterestOptionData());
			return { value: [...interests.value, ...existOption], inputValue: '' };
		}

		const { status, message, data } = await wrapFetchAuth('user/getInterest', {
			method: 'POST',
			body: JSON.stringify({ name }),
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		dispatch(getUserInterestOptionData());

		return { value: [...interests.value, { value: data.id, label: data.name }], inputValue: '' };
	},
);

export const getUserProfessionOptionData = createAction(
	'FETCH_USER_PROFESSION_OPTION_DATA',
	async (name = '') => {
		const { status, message, data } = await wrapFetchAuth(
			'user/getProfession',
			{
				method: 'GET',
			},
			name !== '' ? { name } : {},
		);

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return {
			professions: normalizeArray(data),
		};
	},
);

export const updateUserProfessionOptionData = createAction(
	'UPDATE_USER_PROFESSION_OPTION_DATA',
	(name = '') => async (dispatch, getState) => {
		const { user: { form: { professions } } } = getState();

		const existValue = professions.value.filter(
			item => item.label.toLowerCase() === name.toLowerCase(),
		);

		if (existValue.length > 0) {
			return { value: professions.value };
		}

		const existOption = professions.list
			.filter(id => professions.data[id].name.toLowerCase() === name.toLowerCase())
			.map(id => ({ value: id, label: professions.data[id].name }));

		if (existOption.length > 0) {
			dispatch(getUserProfessionOptionData());
			return { value: [...professions.value, ...existOption], inputValue: '' };
		}

		const { status, message, data } = await wrapFetchAuth('user/getProfession', {
			method: 'POST',
			body: JSON.stringify({ name }),
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		dispatch(getUserProfessionOptionData());

		return { value: [...professions.value, { value: data.id, label: data.name }], inputValue: '' };
	},
);

export const getUserCareerOptionData = createAction(
	'FETCH_USER_CAREER_OPTION_DATA',
	async (name = '') => {
		const { status, message, data } = await wrapFetchAuth(
			'user/getCareer',
			{
				method: 'GET',
			},
			name !== '' ? { name } : {},
		);

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return {
			career: normalizeArray(data),
		};
	},
);

export const updateUserCareerOptionData = createAction(
	'UPDATE_USER_CAREER_OPTION_DATA',
	(name = '') => async (dispatch, getState) => {
		const { user: { form: { career } } } = getState();

		const existValue = career.value.filter(item => item.label.toLowerCase() === name.toLowerCase());

		if (existValue.length > 0) {
			return { value: career.value };
		}

		const existOption = career.list
			.filter(id => career.data[id].name.toLowerCase() === name.toLowerCase())
			.map(id => ({ value: id, label: career.data[id].name }));

		if (existOption.length > 0) {
			dispatch(getUserCareerOptionData());
			return { value: [...career.value, ...existOption], inputValue: '' };
		}

		const { status, message, data } = await wrapFetchAuth('user/getCareer', {
			method: 'POST',
			body: JSON.stringify({ name }),
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		dispatch(getUserCareerOptionData());

		return { value: [...career.value, { value: data.id, label: data.name }], inputValue: '' };
	},
);

export const uploadUserPhoto = createAction('UPLOAD_USER_PHOTO', async e => {
	const files = e.target.files || e.dataTransfer.files;

	const file = files[0];

	const photoData = new FormData();

	photoData.append('file', file, file.name);

	const { status, message, data } = await wrapFetchFormData('user/uploadPhoto', {
		method: 'POST',
		body: photoData,
	});

	if (status !== 200 && status !== 201) {
		throw openFailedAlert('generic', message);
	}

	return { image: data.file_name, imageUrl: data.image_url };
});

export const setIgAccessToken = createAction(
	'SET_IG_ACCESS_TOKEN',
	code => async (dispatch, getState) => {
		const { status, message } = await wrapFetchAuth('user/saveIgAccessToken', {
			method: 'POST',
			body: JSON.stringify({ code, redirect_uri: `${SELF_HOST_ENDPOINT}/auth` }),
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		const { i18n: { lan } } = getState();

		dispatch(replace(`${languageMapRoute(lan)}/user/settings`));
	},
);

export const getIgMedia = createAction('FETCH_IG_MEDIA', async () => {
	const { status, message, data } = await wrapFetchAuth('user/getIgMedia', {
		method: 'GET',
	});

	// No accesstoken
	if (status === 400) {
		return { list: [], data: {}, userIgData: { id: '', username: '' } };
	}

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	const igData = {};
	const userIgData = { id: '', username: '' };

	const list = data.map(item => {
		igData[item.id] = item;
		userIgData.id = item.user.id;
		userIgData.username = item.user.username;

		return item.id;
	});

	return { list, data: igData, userIgData };
});

export const removeIgAccessToken = createAction('REMOVE_IG_ACCESS_TOKEN', async () => {
	const { status, message } = await wrapFetchAuth('user/removeIgAccessToken', {
		method: 'POST',
	});

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}
});

export const getUserOverview = createAction(
	'FETCH_USER_OVERVIEW',
	() => async (dispatch, getState) => {
		const { status, message, data } = await wrapFetchAuth('user/overview', {
			method: 'GET',
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		const level = mapUserLevel(data.level_id);

		if (level === 'REGISTERED') {
			dispatch(openFailedAlert('overview', t('userOverview:incomplete')));
		}

		if (level === 'UNDER_PREVIEW') {
			dispatch(openFailedAlert('overview', t('userOverview:underPreview')));
		}

		if (level === 'DENIED') {
			dispatch(openFailedAlert('overview', t('userOverview:reject')));
		}

		const needCheckinOrders = data.orders.filter(item => item.check_message === 'NEED_CHECKIN');
		const needCheckoutOrders = data.orders.filter(item => item.check_message === 'NEED_CHECKOUT');
		const alertCheckMethod = { orderId: -1, type: '' };

		const { i18n: { lan } } = getState();

		if (needCheckinOrders.length > 0) {
			alertCheckMethod.orderId = needCheckinOrders[0].id;
			alertCheckMethod.type = 'NEED_CHECKIN';

			dispatch(
				openSuccessAlert(
					'overview',
					t('userOverview:needCheckin', {
						startDate: moment(needCheckinOrders[0].date_from)
							.add(7, 'days')
							.format(lan === 'en' ? 'MMM/DD/YYYY' : 'YYYY/MM/DD'),
					}),
				),
			);
		}

		if (needCheckoutOrders.length > 0) {
			alertCheckMethod.orderId = needCheckoutOrders[0].id;
			alertCheckMethod.type = 'NEED_CHECKOUT';

			dispatch(
				openSuccessAlert(
					'overview',
					t('userOverview:needCheckout', {
						endDate: moment(needCheckoutOrders[0].date_to).format(
							lan === 'en' ? 'MMM/DD/YYYY' : 'YYYY/MM/DD',
						),
					}),
				),
			);
		}

		const bookingData = {};
		const list = data.orders.map(item => {
			bookingData[item.id] = keyTransform(item);

			return item.id;
		});

		const reservationData = {};
		const reservationList = data.events.map(item => {
			reservationData[item.id] = item;

			return item.id;
		});

		return { current: list, data: bookingData, reservationData, reservationList, alertCheckMethod };
	},
);



// WEBPACK FOOTER //
// ./src/actions/user.js